import { useCallback, useEffect, useState } from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PortalHeader from '../components/PortalHeader';
import { affiliatesAPI, portalAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { btnGhost, btnPrimary, Field, inputCls, panelCls, StatusBadge } from '../components/ui';

function Stat({ label, value }) {
  return (
    <div className={`${panelCls} p-4`}>
      <div className="text-xs uppercase tracking-wide text-mms-faint">{label}</div>
      <div className="mt-1 text-xl font-semibold mms-num">{value}</div>
    </div>
  );
}

export default function PortalPage() {
  const [me, setMe] = useState(null);
  const [links, setLinks] = useState([]);
  const [withdrawals, setWithdrawals] = useState(null);
  const [target, setTarget] = useState('');
  const [label, setLabel] = useState('');
  const [methodType, setMethodType] = useState('paypal');
  const [methodDetails, setMethodDetails] = useState('');
  const [amount, setAmount] = useState('');

  const load = useCallback(() => {
    portalAPI.me().then(({ data }) => {
      setMe(data);
      if (data.profile?.payout_method) {
        setMethodType(data.profile.payout_method.type);
        setMethodDetails(data.profile.payout_method.details);
      }
    }).catch((err) => toast.error(err.response?.data?.detail || t('Could not load your affiliate profile')));
    portalAPI.links().then(({ data }) => setLinks(data.links)).catch(() => {});
    portalAPI.withdrawals().then(({ data }) => setWithdrawals(data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(t('Copied')));
  };

  const createLink = async () => {
    try {
      await portalAPI.createLink({ target_url: target.trim(), label });
      setTarget(''); setLabel('');
      toast.success(t('Saved'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const saveMethod = async () => {
    try {
      await portalAPI.setPayoutMethod({ type: methodType, details: methodDetails });
      toast.success(t('Saved'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const withdraw = async () => {
    try {
      await portalAPI.requestWithdrawal(Number(amount));
      setAmount('');
      toast.success(t('Withdrawal requested'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Action failed'));
    }
  };

  if (me === null) return <div className="p-6 text-sm text-mms-muted">{t('Loading…')}</div>;
  const shortBase = window.location.origin;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <PortalHeader title={t('Referral center')} />

      <div className={`${panelCls} p-5`}>
        <div className="text-sm text-mms-muted">{t('Your referral link')}</div>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 overflow-x-auto rounded-md border border-mms-border bg-mms-input px-3 py-2 text-sm">
            {me.referral_url}
          </code>
          <button onClick={() => copy(me.referral_url)} className={btnGhost} aria-label={t('Copy')}>
            <Copy size={15} />
          </button>
        </div>
        <div className="mt-1.5 text-xs text-mms-faint">{t('Your code')}: <code>{me.profile?.code}</code></div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={t('Clicks')} value={me.stats?.clicks} />
        <Stat label={t('Unique clicks')} value={me.stats?.unique_clicks} />
        <Stat label={t('Referrals')} value={me.stats?.referrals} />
        <Stat label={t('Cash available')} value={me.balance?.available} />
      </div>

      <div className={`${panelCls} p-5`}>
        <h2 className="mb-3 text-sm font-semibold">{t('Short links')}</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Field label={t('Target URL (brand pages only)')}>
              <input className={inputCls} value={target} placeholder="https://beta.carlosartiles.com/..."
                onChange={(e) => setTarget(e.target.value)} />
            </Field>
          </div>
          <div className="w-40">
            <Field label={t('Label (optional)')}>
              <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} />
            </Field>
          </div>
          <button onClick={createLink} className={btnPrimary}><Plus size={14} /> {t('Create')}</button>
        </div>
        {links.length > 0 && (
          <ul className="mt-4 space-y-2 text-sm">
            {links.map((l) => (
              <li key={l.code} className="flex items-center gap-2">
                <code className="rounded bg-mms-raised px-2 py-1 text-xs">{shortBase}/l/{l.code}</code>
                <button onClick={() => copy(`${shortBase}/l/${l.code}`)} className="text-mms-muted hover:text-mms-text" aria-label={t('Copy')}>
                  <Copy size={13} />
                </button>
                <span className="flex-1 truncate text-xs text-mms-faint">{l.label || l.target_url}</span>
                <span className="mms-num text-xs text-mms-muted">
                  {l.stats?.clicks} {t('clicks')}
                </span>
                <button onClick={() => portalAPI.deleteLink(l.code).then(load)} className="text-mms-muted hover:text-[var(--mms-danger)]" aria-label={t('Delete')}>
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className={`${panelCls} p-5`}>
          <h2 className="mb-3 text-sm font-semibold">{t('Payout method')}</h2>
          {!me.profile?.cash_eligible && (
            <div className="mb-3 rounded-md border border-[var(--mms-warning)] p-3 text-xs">
              <p className="text-[var(--mms-warning)]">
                {me.profile?.tax_status === 'submitted'
                  ? t('Tax document submitted — awaiting review. Cash payouts unlock on approval.')
                  : t('Cash payouts need tax verification first (W-9 / W-8BEN).')}
              </p>
              {me.profile?.tax_status !== 'submitted' && (
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="mt-2 text-xs text-mms-muted"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    affiliatesAPI.uploadTaxDoc(f)
                      .then(() => { toast.success(t('Tax document submitted')); load(); })
                      .catch((err) => toast.error(err.response?.data?.detail || t('Upload failed')));
                  }} />
              )}
            </div>
          )}
          <div className="space-y-3">
            <Field label={t('Type')}>
              <select className={inputCls} value={methodType} onChange={(e) => setMethodType(e.target.value)}>
                <option value="paypal">PayPal</option>
                <option value="wise">Wise</option>
                <option value="bank">{t('Bank transfer')}</option>
              </select>
            </Field>
            <Field label={t('Details (account / email / IBAN)')}>
              <input className={inputCls} value={methodDetails} onChange={(e) => setMethodDetails(e.target.value)} />
            </Field>
            <button onClick={saveMethod} className={btnPrimary}>{t('Save')}</button>
          </div>
        </div>

        <div className={`${panelCls} p-5`}>
          <h2 className="mb-3 text-sm font-semibold">{t('Withdraw')}</h2>
          <p className="mb-3 text-xs text-mms-faint">
            {t('Available')}: <span className="mms-num">{withdrawals?.balance?.available ?? me.balance?.available}</span>
            {' · '}{t('Minimum payout')}: <span className="mms-num">{me.min_payout}</span>
          </p>
          <div className="flex items-end gap-2">
            <div className="w-40">
              <Field label={t('Amount')}>
                <input type="number" step="any" min="0" className={inputCls} value={amount}
                  onChange={(e) => setAmount(e.target.value)} />
              </Field>
            </div>
            <button onClick={withdraw} className={btnPrimary}>{t('Request')}</button>
          </div>
          {withdrawals?.items?.length > 0 && (
            <ul className="mt-4 space-y-1.5 text-sm">
              {withdrawals.items.map((w) => (
                <li key={w.id} className="flex items-center justify-between">
                  <span className="text-xs text-mms-muted">{(w.requested_at || '').slice(0, 10)}</span>
                  <span className="mms-num">{w.amount}</span>
                  <StatusBadge status={w.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
